import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ElementInterface } from '../../Objects/interfaces/AppInterface';
import useTailwindBrand from '../../Theme/useTailwindBrand';
import SelectAuto from '../SelectAuto';
import ButtonField from './ButtonField';

interface SidebarNavLayoutProps {
    data: ElementInterface[];
    children: (value: number) => React.ReactNode;
    defaultValue?: number;
    value?: number;
    onValueChange?: (value: number) => void;
    className?: string;
    classNameBody?: string;
}

const SidebarNavLayout = ({ data, children, defaultValue = 1, value: controlledValue, onValueChange, className, classNameBody }: SidebarNavLayoutProps) => {
    const { classes, common, brandConfig } = useTailwindBrand();
    const location = useLocation();
    const [internalValue, setInternalValue] = useState<number>(defaultValue);

    const isControlled = controlledValue !== undefined;

    // defaultValue n'est lu qu'au montage : une nouvelle navigation vers la page déjà
    // affichée qui demande une rubrique (state.step, ex. notification) y ramène donc
    // explicitement. Les autres rendus et la navigation dans le menu ne sont pas touchés.
    const [syncedLocationKey, setSyncedLocationKey] = useState(location.key);
    if (location.key !== syncedLocationKey) {
        setSyncedLocationKey(location.key);
        const requestedStep = (location.state as { step?: unknown } | null)?.step;
        if (!isControlled && requestedStep !== undefined && requestedStep !== null) {
            setInternalValue(defaultValue);
        }
    }

    const value = isControlled ? controlledValue : internalValue;

    const handleChange = (newValue: number, action?: () => void, completed?: boolean, id?: string, disabledAction?: boolean) => {
        if (completed) return;
        if (id && id === 'valid-compte') return;
        if (disabledAction) return;
        if (newValue !== undefined && newValue !== null) {
            if (!isControlled) setInternalValue(newValue);
            onValueChange?.(newValue);
        } else {
            action?.();
        }
    };

    const isParentActive = (row: ElementInterface): boolean =>
        row.value === value || (row.children?.some(c => c.value === value) ?? false);

    return (
        <div className="flex flex-col lg:flex-row gap-6">
            <Card className={`hidden lg:block flex-shrink-0 ${className ?? 'w-full lg:w-56 xl:w-64'}`}>
                <CardContent>
                    <div className="p-3 border-b border-gray-100">
                        <h2
                            className="text-sm font-semibold text-gray-500 uppercase tracking-wider"
                            style={{ fontFamily: brandConfig.fontFamily }}
                        >
                            Menu
                        </h2>
                    </div>
                    <ul className="p-2 space-y-1">
                        {data.map((row: ElementInterface | any, key) => {
                            const parentActive = isParentActive(row);
                            const hasChildren = row.children && row.children.length > 0;
                            return (
                                <li
                                    key={key}
                                    className={row.classCustom ?? 'flex flex-col'}
                                >
                                    <ButtonField
                                        variant="text"
                                        onClick={() => handleChange(row.value, row.action, row.completed, row.id, row.disabledAction)}
                                        label={row.title}
                                        startIcon={row.icons || row.icon}
                                        endIcon={row.completed ? <CheckCircleIcon className="text-green-500" /> : undefined}
                                        style={{
                                            fontFamily: brandConfig.fontFamily,
                                            ...(parentActive
                                                ? { backgroundColor: `${brandConfig.colors.primary}18`, color: brandConfig.colors.primary }
                                                : {}),
                                        }}
                                        className={`${row.locked ? 'text-gray-400 cursor-not-allowed' : ''} ${row.completed ? `${classes.primaryText} cursor-not-allowed` : ''} w-full flex justify-start items-center gap-x-3 font-bold rounded-xl px-4 py-2.5 text-left md:text-base text-sm transition-all duration-200 ${
                                            parentActive
                                                ? 'shadow-sm'
                                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                        }`}
                                        disabled={row.locked || row.completed}
                                    />

                                    {/* Sub-menu items */}
                                    {hasChildren && (
                                        <ul
                                            className="ml-5 mt-0.5 mb-1 space-y-0.5 border-l-2 pl-3"
                                            style={{ borderColor: `${brandConfig.colors.primary}35` }}
                                        >
                                            {row.children.map((child: Omit<ElementInterface, 'children'>, childKey: number) => {
                                                const childActive = child.value === value;
                                                return (
                                                    <li key={childKey}>
                                                        <button
                                                            onClick={() => {
                                                                if (!child.locked && !child.completed && child.value !== undefined) {
                                                                    handleChange(child.value, child.action, child.completed, child.id);
                                                                } else if (child.action && child.value === undefined) {
                                                                    child.action();
                                                                }
                                                            }}
                                                            disabled={!!child.locked}
                                                            className={`w-full text-left px-2 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                                                                childActive
                                                                    ? 'font-semibold'
                                                                    : child.completed
                                                                        ? 'cursor-default'
                                                                        : child.locked
                                                                            ? 'text-gray-400 cursor-not-allowed'
                                                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                                            }`}
                                                            style={{
                                                                fontFamily: brandConfig.fontFamily,
                                                                ...(
                                                                    // childActive
                                                                    // ? {
                                                                    //     // backgroundColor: `${brandConfig.colors.primary}12`,
                                                                    //     color: brandConfig.colors.primary
                                                                    // }
                                                                    //     :
                                                                        child.completed
                                                                        ? { color: '#16a34a' }
                                                                        : {}),
                                                            }}
                                                        >
                                                            {child.completed ? (
                                                                <CheckCircleIcon style={{ fontSize: 12, color: '#16a34a', flexShrink: 0 }} />
                                                            ) : (
                                                                <span
                                                                    style={{
                                                                        width: 6,
                                                                        height: 6,
                                                                        borderRadius: '50%',
                                                                        flexShrink: 0,
                                                                        display: 'inline-block',
                                                                            backgroundColor:
                                                                            //     childActive
                                                                            // ? brandConfig.colors.primary
                                                                            //         :
                                                                                    child.locked
                                                                                ? '#d1d5db'
                                                                                : '#9ca3af',
                                                                    }}
                                                                />
                                                            )}
                                                            <span>{child.title}</span>
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </CardContent>
            </Card>

            <div className="lg:hidden">
                <SelectAuto
                    data={data}
                    value={value}
                    setValue={setInternalValue}
                    customClass={`bg-white ${classes.text} shadow-sm border border-gray-200 rounded-md p-3 w-full ${common.body}`}
                />
            </div>

            <main className={`flex-1 min-w-0 ${classNameBody}`}>
                {children(value)}
            </main>
        </div>
    );
};

export default SidebarNavLayout;
